<template>
  <AnswerPage :page="page" />
</template>

<script setup lang="ts">
const page = {
  slug: '/workspace369-features',
  metaTitle: 'Workspace369 Features: Proposals, Booking, Payments, CRM, AI',
  description:
    'Explore Workspace369 features: branded proposals, e-signature, deposits, booking pages, client portal, CRM, projects, invoices, inbox, AI and automations.',
  eyebrow: 'Feature answer',
  h1: 'Workspace369 features, explained clearly.',
  lede:
    'Workspace369 combines the core systems a client-work business usually has to buy separately: CRM, delivery, communication, billing, payments, files, AI, and reporting.',
  aiAnswer:
    'Workspace369 features include CRM, requests, projects, tasks, time tracking, calendar and online booking, an email inbox, SMS, Workspace Voice and voicemail, proposals, contracts, invoices, payments, retainers, expenses, accounting, inventory, files, AI, workflow automations, dashboards and reporting. Some start on higher plans, and the list below says which.',
  facts: [
    { label: 'Main suite', value: 'CRM, projects, billing, communication, AI, finance, and reporting' },
    { label: 'Communication', value: 'Inbox, SMS, MMS, voice, voicemail, call records, templates, and AI summaries' },
    { label: 'Finance', value: 'Quotes, invoices, retainers, payment plans, expenses, accounting, inventory, and AR' },
  ],
  summaryKicker: 'Feature map',
  summaryTitle: 'The suite is designed around the client lifecycle.',
  summary: [
    {
      kicker: 'Front office',
      title: 'Manage clients, requests, inbox, SMS, and voice.',
      copy:
        'Every conversation and request can stay near the client record instead of disappearing into separate communication tools.',
    },
    {
      kicker: 'Delivery',
      title: 'Run projects, tasks, schedules, files, and notes.',
      copy:
        'Teams can manage work, documentation, reminders, assignments, and handoffs without leaving the workspace.',
    },
    {
      kicker: 'Finance',
      title: 'Connect quotes, invoices, payments, expenses, and reports.',
      copy:
        'Billing and operational reporting sit closer to the work that created the revenue.',
    },
  ],
  workflowKicker: 'Lifecycle',
  workflowTitle: 'How the feature set works together.',
  workflow: [
    { title: 'Lead and client', copy: 'Capture client details, lead source, notes, tags, and communication history.' },
    { title: 'Scope and deliver', copy: 'Use requests, projects, tasks, calendar, files, and proposals to organize the work.' },
    { title: 'Bill and collect', copy: 'Send proposals, invoices, retainers, payment plans, and payment links.' },
    { title: 'Automate and report', copy: 'Use AI, workflow automations, dashboards, accounting, inventory, and reporting.' },
  ],
  modulesKicker: 'Full list',
  modulesTitle: 'Every feature category in Workspace369',
  modulesCopy:
    'A plain list of what Workspace369 includes. Some features start on higher plans; the pricing page shows which plan includes each one.',
  modules: [
    'Client CRM',
    'Requests',
    'Projects',
    'Task boards',
    'Calendar and scheduling',
    'Online booking',
    'Inbox',
    'Two-way SMS and MMS',
    'Workspace Voice',
    'Voicemail transcription',
    'AI call summaries',
    'Time tracking',
    'Invoices',
    'Quotes',
    'Contracts and e-signature',
    'Advanced proposals',
    'Payment plans',
    'Split payments',
    'Expenses',
    'Accounting',
    'Product catalog',
    'Inventory tracking',
    'Files and versioning',
    'Client portals',
    'Automations',
    'API integrations',
    'Audit trails',
    'Theme management',
    'Dashboard customization',
  ],
  featureAreasKicker: 'By plan',
  featureAreasTitle: 'What each area includes, and where it starts',
  featureAreasCopy:
    'Plans run Cadet, Voyager, Specialist, Commander and Enterprise. Each area below says what is on every plan and what starts higher, from the same plan comparison the pricing page uses.',
  featureAreas: [
    {
      name: 'CRM and requests',
      copy: 'Client records, companies, custom fields, public intake forms with a drag-and-drop builder, request tracking, and turning a request into a client.',
      plans: 'Every plan. Lead source reports from Voyager; client lifetime value from Specialist.',
      path: '/solutions/workspace-crm',
      linkLabel: 'CRM with invoicing',
    },
    {
      name: 'Projects, tasks and time',
      copy: 'Projects with stages, kanban boards and task lists, subtasks, task time estimates, timers, timesheets with approvals, and billable time that goes straight onto an invoice.',
      plans: 'Every plan, with a project limit on Cadet. Task dependencies, timeline view and project financials from Voyager; team workload view from Commander.',
      path: '/features/time-tracking',
      linkLabel: 'Time tracking',
    },
    {
      name: 'Calendar and online booking',
      copy: 'A team calendar with recurring events and attendees, appointment reminders, and booking pages clients use to pick a time.',
      plans: 'Calendar and email reminders on every plan; text reminders from Voyager. Online booking, availability rules and round-robin scheduling from Specialist.',
      path: '/features/online-booking',
      linkLabel: 'Online booking',
    },
    {
      name: 'Email inbox',
      copy: 'Email matched to the client, with templates, quick responses and signatures, scheduled messages, and tasks created from messages.',
      plans: 'Every plan. Shared inbox, conversation assignment and private team comments from Voyager.',
      path: '/features/communications',
      linkLabel: 'Inbox and communications',
    },
    {
      name: 'Texts, calls and voicemail',
      copy: 'Two-way SMS and MMS, group texting, missed-call text back, calling from the client record, and voicemail, all on the client record.',
      plans: 'From Voyager. IVR menus, call queues, call recording, AI voicemail transcription and AI call summaries from Specialist.',
      path: '/features/workspace-voice',
      linkLabel: 'Workspace Voice',
    },
    {
      name: 'Proposals and contracts',
      copy: 'Proposals built from your catalog, with optional add-on items, open tracking, a deposit on acceptance, and contracts clients sign online.',
      plans: 'Every plan. Cover images, page backgrounds and proposal version history from Specialist.',
      path: '/features/quoting',
      linkLabel: 'Proposals and quotes',
    },
    {
      name: 'Invoices and payments',
      copy: 'Invoices, recurring invoices, retainers with hour banks, payment links, card and ACH payments through your own Stripe account, split payments, and card autopay.',
      plans: 'Every plan. Several invoice layouts from Voyager; change orders, payment plans and refunds from Specialist. Credit notes are coming soon.',
      path: '/features/invoices',
      linkLabel: 'Invoices',
    },
    {
      name: 'Expenses and accounting',
      copy: 'Expense tracking with receipt capture, a chart of accounts, AR aging, project and customer profitability, and full books with financial statements.',
      plans: 'Expenses and basic accounting from Specialist. Full accounting, with accounts payable, the P&L, balance sheet and cash flow, and period close, from Commander.',
      path: '/features/accounting',
      linkLabel: 'Accounting',
    },
    {
      name: 'Products and inventory',
      copy: 'A product and service catalog for proposals and invoices, plus stock levels, adjustments and low-stock alerts.',
      plans: 'Catalog on every plan, with a limit on Cadet. Inventory tracking from Specialist.',
      path: '/features/products',
      linkLabel: 'Products',
    },
    {
      name: 'Workflow automations',
      copy: 'Workflows started by 53 triggers, with filters, timed waits, unlimited steps, ready-made templates, test runs, and late-payment reminders.',
      plans: 'Every plan: email-only with up to 3 active workflows on Cadet, 10 with text steps on Voyager, 25 on Specialist, no limit on Commander and Enterprise. AI-drafted workflows and automatic late fees from Commander.',
      path: '/features/automation',
      linkLabel: 'Workflow automations',
    },
    {
      name: 'AI',
      copy: 'AI chat, writing and rewriting, knowledge search, translation and thread summaries, AI Import for CSV and Excel files, and connections to Claude, ChatGPT and other AI apps.',
      plans: 'Every plan, with a monthly AI usage allowance that grows on each higher plan. The AI daily briefing from Voyager.',
      path: '/features/ai-chat',
      linkLabel: 'AI chat',
    },
    {
      name: 'Files, branding and access',
      copy: 'File management with versioning, branded documents and PDFs, themes, roles and permissions, and client-scoped access for people who should only see some clients.',
      plans: 'Every plan. Full role-based access control from Voyager; folder permissions, share-link expiry and a file access log from Specialist.',
      path: '/features/files',
      linkLabel: 'Files',
    },
    {
      name: 'Dashboards and reports',
      copy: 'Dashboards and reports, custom dashboards, attention signals, the client and team activity view, and team productivity reports.',
      plans: 'Every plan.',
      path: '/features/analytics',
      linkLabel: 'Analytics',
    },
    {
      name: 'Integrations',
      copy: 'Google Calendar, CSV import, AI app connections, and webhooks and the API for tools like Zapier, Make and n8n.',
      plans: 'Every plan. Webhooks and the API on every plan.',
      path: '/integrations',
      linkLabel: 'All integrations',
    },
  ],
  integrationLinksKicker: 'Integrations',
  integrationLinksTitle: 'Connect Workspace369 to your automation tools.',
  integrationLinksCopy:
    'Webhooks and the API on every plan, connect Workspace369 to the tools below today; native apps are in development.',
  integrationLinks: [
    { label: 'Connect Workspace369 to Zapier', path: '/integrations/zapier' },
    { label: 'Connect Workspace369 to Make (Integromat)', path: '/integrations/make' },
    { label: 'Workspace369 n8n integration', path: '/integrations/n8n' },
  ],
  showPricing: true,
  faqTitle: 'Feature questions',
  faq: [
    {
      question: 'Does Workspace369 include CRM and invoicing?',
      answer:
        'Yes. Workspace369 includes client CRM and invoicing workflows, plus proposals, quotes, payment plans, payments, projects, communication, files, AI, and reporting depending on plan.',
    },
    {
      question: 'Does Workspace369 include phone and SMS features?',
      answer:
        'Yes. Workspace369 includes inbox-suite communication features such as SMS, MMS, Workspace Voice, voicemail, call history, routing, and AI summaries depending on plan and setup.',
    },
    {
      question: 'Does Workspace369 support growing teams?',
      answer:
        'Yes. Workspace369 plans run from one seat on Cadet to 50 included seats on Enterprise, with broader AI, automation, accounting, and operational controls on the higher tiers. Team plans can add seats.',
    },
    {
      question: 'Which Workspace369 features are on every plan?',
      answer:
        'CRM and requests, projects and time tracking, the calendar, the email inbox, proposals and contracts, invoices and payments, workflow automations, AI, files, dashboards and email support are on every plan, including Cadet. Higher plans add capacity and the features listed above.',
    },
    {
      question: 'Which plan do I need for texting and calls?',
      answer:
        'Texts, calls and voicemail start on Voyager. The phone system, with IVR menus, call queues and call recording, starts on Specialist.',
    },
  ],
};
</script>
